/**
 * watchlist.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { Algorithm } from "../algorithms/structure/algorithm";
import { AlertService } from "../common/alert.service";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { ModalHelpers, arrayIntersects, oneline, hardEquals } from "app/support/utilities";
import { SettingService } from "app/configuration/setting.service";
import { Subject } from "../subjects/subject";
import { SubjectContextType } from "../subjects/subject-context-type";
import { WatchlistEntry, WatchlistTable } from "./watchlist-entry";
import { WatchlistService } from "./watchlist.service";
import { WorklistConfigModalComponent }
  from "app/server/messages/watchlists/worklist-config-modal.component";
import { ModalService } from "app/common/modal.service";
import { SortTarget } from "app/common/data/data-sorting-header.component";
import { TableDescription } from "app/server/messages/export";
import { UserService } from "app/auth/identity/user.service";
import { Rule } from "app/auth/permissions/rules";
import { Router } from "@angular/router";

/**
 * The {@link WatchlistComponent} enumerates the content of a user's watchlist,
 * stratified by subject context type. The tables are sortable, and each entry
 * provides a summary and actions, such as opening the context, removing it,
 * writing notes, or changing notification settings.
 */
@Component({
  selector: "watchlist",
  template: `
    <h2 *ngIf="showTitle">
      Work List
      <span class="badge warning">{{watchlist.activePersonalCount()}} unread</span>
      <span class="badge">{{watchlist.personalEntriesById.size - watchlist.activePersonalCount()}} read</span>
      <div class="pull-right btn btn-primary" [class.disabled]="!canViewShared()" (click)="viewShared()">
        Browse Shared Lists...
      </div>
    </h2>
    <div *ngFor="let tab of watchlist.entryTables">
    <table
      *ngIf="!enforcedType || tab.contextType === enforcedType"
      class="table table-responsive">
      <caption
        *ngIf="watchlist.entryTables.length > 1"
        class="type capitalized">
        {{tab.contextType}}
      </caption>
      <thead>
        <tr>
          <th
            class="grabber"
            [sorts]="tab.entries"
            [dot]="FOLDER_SORT"
            storedAs="worklistSorting"
            asType="string"
            [class.hidden]="foldersHidden()">
            <span class="glyphicon glyphicon-tag"></span>
          </th>
          <th
            *ngFor="let column of tab.columns; let i = index"
            [sorts]="tab.entries"
            [dot]="'summary.' + i"
            storedAs="worklistSorting"
            [class.capitalized]="true"
            [class.hidden]="ignoredColumns.has(column)">
            {{column}}
          </th>
          <th
            [sorts]="tab.entries"
            dot="lastObserved"
            storedAs="worklistSorting"
            asType="integer">
            Last Viewed
          </th>
          <th [class.hidden]="notesHidden()">
            Notes
            <div class="subtitle">click to edit</div>
          </th>
          <th [class.hidden]="notificationsHidden()">
            Notify via
            <div class="subtitle">click to change</div>
          </th>
          <th colspan="1" class="text-center">
            <button
              class="btn btn-sm btn-default"
              (click)="openConfigModal()">
              Configure Columns...
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        <template
          ngFor
          let-entry
          [ngForOf]="(tab.entries | paginate)">
        <tr
          #folder
          *ngIf="folderMode(tab) && firstOfFolder(entry, tab)"
          class="item-category"
          (dragover)="allowDrag($event); folder.highlight = true"
          (dragenter)="allowDrag($event); folder.highlight = true"
          (dragleave)="folder.highlight = false"
          (drop)="dropItem($event, entry.categories[0], tab); folder.highlight = false"
          [class.drag-target]="folder.highlight"
          [class.droppable]="folder.highlight"
          [class.info]="entry.categories[0]"
          [class.warning]="!entry.categories[0]">
          <td>
            <span class="glyphicon glyphicon-folder-open"></span>
          </td>
          <td colspan="20">
            {{entry.categories[0] || 'Unsorted'}}
          </td>
        </tr>
        <tr [class.bg-warning]="entry.alert">
          <td
            *ngIf="folderMode(tab) && !foldersHidden()"
            class="grabber grabbable"
            draggable="true"
            (dragstart)="startDrag($event, entry.watchId)">
            <span class="glyphicon glyphicon-resize-vertical"></span>
          </td>
          <td
            *ngIf="!folderMode(tab) && !foldersHidden()"
            (click)="assignFolder(entry)">
            <span class="badge" [class.primary]="entry.categories[0]">
              {{entry.categories[0] || "unsorted"}}
            </span>
          </td>
          <td
            *ngFor="let data of entry.summary"
            [class.hidden]="ignoredColumns.has(data.displayName)">
            <typed-display [input]="data"></typed-display>
          </td>
          <td
            [title]="entry.alert ? 'Changes since you last viewed' : ''">
            <div [class.emph]="entry.alert">
              {{entry.lastObserved | utcDate}}
            </div>
            <a
              class="btn btn-xs btn-warning"
              *ngIf="entry.alert"
              (click)="dismiss(entry)">
              mark as read
            </a>
          </td>
          <td #noteCell [class.hidden]="notesHidden()">
            <div
              *ngIf="!noteCell.editing"
              class="focus-text"
              (click)="toggle(noteCell, text, button)"
              >{{entry.note || "n/a"}}</div>
            <div [class.hidden]="!noteCell.editing" class="input-group">
              <textarea
                autosize
                [formControl]="entry.control"
                class="form-control">
              </textarea>
              <div class="input-group-btn">
                <button class="btn btn-info"
                  #button
                  (click)="saveNote(entry, noteCell)">
                  Save
                </button>
              </div>
            </div>
          </td>
          <td
            (click)="changeNotificationSetting(entry)"
            [class.hidden]="notificationsHidden()">
            <span *ngIf="entry.emailNotifyOnUpdate">
              <span class="glyphicon glyphicon-envelope"></span>
              <code>email</code>
            </span>
            <span *ngIf="!entry.emailNotifyOnUpdate">
              <span class="glyphicon glyphicon-volume-off"></span>
              <code>none</code>
            </span>
          <td class="text-center action-cell">
            <div role="group">
              <button
                *ngIf="entry.watchId === null"
                class="btn btn-sm btn-primary"
                title="You do not have permission to view this data"
                disabled>
                {{asSelector ? "Use" : "Open"}}
              </button>
              <a
                *ngIf="entry.watchId !== null && asSelector"
                class="btn btn-sm btn-primary"
				(click)="open(entry)">
                <span class="hidden-sm hidden-xs">Use</span>
                <span
                  class="hidden-md hidden-lg glyphicon glyphicon-folder-open">
                </span>
			        </a>
              <div
                #openOptions
                *ngIf="entry.watchId !== null && !asSelector"
                class="btn-group"
                [class.open]="openOptions.open"
                (mouseleave)="openOptions.open = false">
                <a
                  class="btn btn-sm btn-primary"
                  (click)="openDefault(entry)">
                  <span class="hidden-sm hidden-xs">Open</span>
                  <span
                  class="hidden-md hidden-lg glyphicon glyphicon-folder-open">
                  </span>
                </a>
                <button
                  type="button"
                  class="btn btn-sm btn-primary dropdown-toggle"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  [attr.aria-expanded]="!!openOptions.open"
                  (click)="openOptions.open = !openOptions.open">
                  <span class="caret"></span>
                </button>
                <ul class="dropdown-menu right-edge">
                  <li>
                    <a (click)="open(entry, null)">
                      Summary Page
                    </a>
                  </li>
                  <li role="separator" class="divider"></li>
                  <li *ngFor="let alg of algorithms.bySCType(tab.contextType)">
                    <a (click)="open(entry, alg)">
                      {{alg.displayName}}
                    </a>
                  </li>
                </ul>
              </div>
              <a
                class="btn btn-sm btn-danger"
                (click)="unwatch(entry)">
                <span class="hidden-sm hidden-xs">Remove</span>
                <span class="hidden-md hidden-lg glyphicon glyphicon-remove">
                </span>
              </a>
            </div>
          </td>
        </tr>
        </template>
      </tbody>
      <caption>
        <pager [array]="tab.entries" [asNeeded]="true"></pager>
      </caption>
    </table>
    </div>
    <h4 class="text-center" *ngIf="watchlist.entryTables.length === 0">
      Your work list is empty!
    </h4>
`,
  styles: [`
    caption {
      caption-side: bottom;
      font-size: 1.7em;
      font-style: italic;
      padding: 0.5em;
    }
    caption.empty {
      text-align: center;
    }
    caption.type {
      caption-side: top;
      margin: 0 20px;
      text-align: left;
    }
    .faded, .grabber {
      color: #DDDDDD;
    }
    table.table>tbody>tr>td {
      vertical-align: middle;
    }
    .form-control.focus-only {
      border: 1px transparent;
      box-shadow: none;
    }
    .form-control.focus-only:focus, .form-control.focus-only:hover  {
      border: 1px solid #ccc;
    }
    .hidden {
      display: none;
    }
    textarea {
      resize: none;
    }
    .focus-text {
      white-space: pre-line;
      border: 1px solid transparent;
      border-radius: 5px;
      padding: 5px;
      min-height: 34px;
      width: 100%;
    }
    .focus-text:hover {
      border: 1px solid #ccc;
    }
    th .subtitle {
      font-size: 80%;
      font-weight: normal;
    }
    .btn-group {
      overflow-y: none;
	}
	.action-cell {
		min-width: 96px;
  }
  .item-category {
    font-style: italic;
  }
  .item-category.unsorted {
    color: gray;
  }
  .droppable {
    cursor: copy;
  }
  .grabbable {
    cursor: grab;
  }
  .drag-target {
    font-weight: bold;
  }
  .badge.primary {
    background: #1D3559;
    border-color: lighten(#1D3559, 10%);
  }
  `]
})
export class WatchlistComponent
{
  @Input()
  public enforcedType : SubjectContextType;

  @Input()
  public showTitle : boolean = true;

  @Input()
  public asSelector : boolean = false;

  @Output()
  public selection : EventEmitter<Subject> = new EventEmitter();

  public ignoredColumns : Set<string> = this.getIgnored();

  private getIgnored () : Set<string>
  {
    const s : string = this.settings.get("ignored worklist columns") || "";
    if (s === "")
    {
      return new Set();
    }
    return new Set(s.split(","));
  }

  public openConfigModal () : void
  {
    const columns : string[] =
      [
        WatchlistComponent.NotesColumnLabel,
        WatchlistComponent.NotificationColumnLabel
      ].concat(this.watchlist.knownColumns);
    this.modals.observe(
      WorklistConfigModalComponent,
      {
          columns,
          hidden: Array.from(this.ignoredColumns)
      }
    ).subscribe((result : any) => {
      if (Array.isArray(result))
      {
        this.ignoredColumns = new Set(result);
        this.settings.set("ignored worklist columns", result.join(","));
      }
      else
      {
        console.warn("Got unexpected non-array from modal result:", result);
      }
    });
  }

  public static NotesColumnLabel : string = "(notes)";
  public static NotificationColumnLabel : string = "(notify via)";
  public static FolderColumnLabel : string = "(folder)";
  public notesHidden () : boolean
  {
    return this.ignoredColumns.has(WatchlistComponent.NotesColumnLabel);
  }
  public notificationsHidden () : boolean
  {
    return this.ignoredColumns.has(WatchlistComponent.NotificationColumnLabel);
  }
  public foldersHidden () : boolean
  {
    return this.ignoredColumns.has(WatchlistComponent.FolderColumnLabel);
  }

  public readonly FOLDER_SORT : string = "categories.0";
  public folderMode (table : WatchlistTable) : boolean
  {
    const sorting : SortTarget = table.entries;
    return sorting._sort && sorting._sort.dot === this.FOLDER_SORT;
  }

  public constructor (
    public watchlist : WatchlistService,
    public algorithms : AlgorithmService,
    public userSvc : UserService,
    public settings : SettingService,
    public modals : ModalService,
    public router : Router
  ) {/* DI */}

  /**
   * Remove a currently-watched item from the watchlist
   * @param entry entry to be removed
   */
  public unwatch (entry : WatchlistEntry) : void
  {
    this.watchlist.unwatch(entry).subscribe(
      (result : boolean) => {
        if (!result)
        {
          this.modals.confirm({
            title: "Unsubscribe failed",
            body: `The server failed to unsubscribe you from the record. It
                  could be that this is because the record has been deleted
                  or migrated. Do you want to attempt to forcefully clean up
                  remaining subscription data?`,
            yesLabel: "Forcefully Remove",
            cancelLabel: "Nevermind",
            dismissValue: false
          }).subscribe((retry : boolean) => {
            if (retry)
            {
              this.watchlist.unwatch(entry, true, true).subscribe();
            }
          });
        }
      }
    );
  }

  /**
   * Save edits made to a watchlist's free-text `note` field
   * @param entry The watchlist entry
   * @param notecell The view container for the cell
   */
  public saveNote (entry : WatchlistEntry, notecell : any) : void
  {
    this.watchlist.editNote(entry, entry.control.value).subscribe(
      (success : boolean) => notecell.editing = !success
    );
  }

  /**
   * Toggle the editing status of a note (visually alternating between static
   * text and a fillable textarea).
   * @param notecell A view wrapper for the note fields
   * @param t The HTML textarea element
   * @param b The HTML button element
   */
  public toggle (notecell : any, t : HTMLElement, b : HTMLElement) : void
  {
    notecell.editing = !notecell.editing;
  }

  /**
   * Toggle whether the user gets email notifications for a watchlist item.
   * @param entry The entry to save
   */
  public changeNotificationSetting (entry : WatchlistEntry) : void
  {
    this.watchlist.changeNotificationSetting(entry, !entry.emailNotifyOnUpdate)
      .subscribe((success : boolean) =>
        entry.emailNotifyOnUpdate = (entry.emailNotifyOnUpdate !== success));
        // this is XORing the operands; when successful, invert the value
  }

  public open (entry : WatchlistEntry, target? : Algorithm) : void
  {
    this.watchlist.open(entry, !this.asSelector, target).subscribe(
      (subject : Subject) => this.selection.emit(subject));
  }

  public openDefault (entry : WatchlistEntry) : void
  {
    const target : Algorithm | null =
      this.algorithms.defaultForOpen(entry.contextType);
    this.open(entry, target);
  }

  public dismiss (entry : WatchlistEntry) : void
  {
    this.watchlist.clearAlert(entry).subscribe();
  }

  public static DRAG_ITEM_TYPE : string = "application/vnd.mcp-workitem";

  public startDrag (event : DragEvent, id : number) : void
  {
    event.dataTransfer.setData(WatchlistComponent.DRAG_ITEM_TYPE, `${id}`);
    event.dataTransfer.effectAllowed = "move";
  }

  public allowDrag (event : DragEvent, toFlag? : any) : boolean
  {
    if (toFlag)
    {
      toFlag.highlight = true;
    }
    if (event.dataTransfer.types.indexOf(
      WatchlistComponent.DRAG_ITEM_TYPE) > -1)
    {
      event.dataTransfer.effectAllowed = "move";
      event.preventDefault();
      return false;
    }
    return true;
  }

  public dropItem (event : DragEvent, folder : string, table : WatchlistTable) 
    : void
  {
    const id : number = Number.parseInt(
      event.dataTransfer.getData(WatchlistComponent.DRAG_ITEM_TYPE), 10);
      const entry : WatchlistEntry = this.watchlist.personalEntriesById.get(id);
    this.watchlist.editNote(entry, entry.note, (folder ? [folder] : [])).subscribe(
      (ok : boolean) => {
        if (ok)
        {
          entry.categories = [folder];
          this.moveToFrontOfFolder(entry, table);
        }
      });
  }

  private moveToFrontOfFolder (entry : WatchlistEntry, table : WatchlistTable)
   : void
  {
    const source : number = table.entries.findIndex(hardEquals(entry));
    if (source === -1)
    {
      return console.warn(
        `ID ${entry.watchId} not found in table ${table.contextType}`);
    }
    const target : number = table.entries.findIndex((e : WatchlistEntry) =>
      e.categories && e.categories[0] === entry.categories[0] && e !== entry);
    table.entries.splice(target, 0, ...table.entries.splice(source, 1));
  }

  public assignFolder (entry : WatchlistEntry) : void
  {
    this.modals.stringPrompt({
      title: "Assign a folder",
      body: oneline`
        Choose a folder for
        ${Subject.readableTitleFor(entry.summary, entry.contextType)}.
        (By entering a new name, you will create a new folder.)`,
      placeholder: "Leave empty to unassign",
      acceptEmptyResult: true
    }).subscribe((label : string) => {
      this.watchlist.editNote(entry, entry.note, (label ? [label] : [])).subscribe(
        (ok : boolean) => {
          if (ok)
          {
            entry.categories = [label];
          }
        });
    });
  }

  public firstOfFolder (entry : WatchlistEntry, table : WatchlistTable)
    : boolean
  {
    const i : number = table.entries.findIndex(
      (x : WatchlistEntry) => x === entry);
    if (i === -1)
    {
      console.error("Cannot find entry in table:", entry);
      return false;
    }
    if (i === 0)
    {
      return true;
    }
    const prev : WatchlistEntry = table.entries[i - 1];
    return entry.categories[0] !== prev.categories[0];
  }

  public canViewShared ()
  {
    return this.userSvc.self.hasRule(Rule.VIEW_SHARED_WORKLIST);
  }

  public viewShared ()
  {
    if (!this.canViewShared()) {
      this.modals.alert(`You do not have permission to view shared worklists!`);
    } else {
      this.router.navigate(['lists']);
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/watchlist/watchlist.component.ts